import { Box, Typography, useTheme } from "@mui/material";
import { PaginationNotificationList } from "../../redux/slice/inbox/createNotificationSlice";
import { NotificationData } from "../operation/notification/NotificationList";
import DashboardNotificationItem from "./DashboardNotificationItem";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";

type NotificationTab = "unread" | "acknowledged";

interface CommunicationNotificationsPanelProps {
  notifTab: NotificationTab;
  onTabChange: (tab: NotificationTab) => void;
  isUnread: boolean;
  onRefresh: () => void;
  grouped: Record<string, NotificationData[]>;
  sortedGroups: string[];
  tabArray: NotificationTab[];
  NotificationListInfo: PaginationNotificationList | null | undefined;
  rowHeight?: number;
  maxRows?: number;
  headerAdornment?: React.ReactNode;
}

export const CommunicationNotificationsPanel = ({
  notifTab,
  onTabChange,
  isUnread,
  onRefresh,
  grouped,
  sortedGroups,
  tabArray,
  NotificationListInfo,
  rowHeight,
  maxRows,
  headerAdornment,
}: CommunicationNotificationsPanelProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const isDark = theme.palette.mode === "dark";
  const cardBg = isDark ? "#191c24" : "#ffffff";
  const borderColor = isDark ? "rgba(255,255,255,0.07)" : "#e5e9ed";
  const rowBorder = isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9";

  const totalForViewAll = maxRows ?? 5;

  const GroupDividerLabel = ({ label }: { label: string }) => (
    <Box
      sx={{
        position: "absolute",
        top: -11,
        left: 0,
        right: 0,
        display: "flex",
        alignItems: "center",
        px: 3,
        pointerEvents: "none",
        zIndex: 5,
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.6,
          px: 1.2,
          py: 0.3,
          borderRadius: "999px",
          backgroundColor: cardBg,
          border: `1px solid ${
            isDark ? "rgba(20,184,166,0.35)" : "rgba(20,184,166,0.25)"
          }`,
        }}
      >
        <Box
          sx={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            backgroundColor: "#14b8a6",
          }}
        />
        <Typography
          sx={{
            fontSize: "0.63rem",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: "#14b8a6",
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );

  // Flatten all items across sorted groups, capped at maxRows if provided
  const allItems: { item: NotificationData; group: string; isFirstInGroup: boolean }[] = [];
  for (const group of sortedGroups) {
    // const groupItems = grouped[group];   // CS-1984: guarded below
    const groupItems = grouped[group] ?? [];   // CS-1984
    for (let idx = 0; idx < groupItems.length; idx++) {
      if (maxRows != null && allItems.length >= maxRows) break;
      allItems.push({ item: groupItems[idx], group, isFirstInGroup: idx === 0 });
    }
    if (maxRows != null && allItems.length >= maxRows) break;
  }

  return (
    <Box
      sx={{
        backgroundColor: cardBg,
        borderRadius: 3,
        border: `1px solid ${borderColor}`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          borderBottom: `1px solid ${rowBorder}`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
          <Typography
            fontWeight={700}
            sx={{
              color: isDark ? "#e5e9ed" : "#1e293b",
              fontSize: "1.15rem",
            }}
          >
            Notifications
          </Typography>
          {headerAdornment}
        </Box>

        <Box sx={{ display: "inline-flex" }}>
          {tabArray.map((t, i) => (
            <Box
              key={t}
              onClick={() => onTabChange(t)}
              sx={{
                px: 2.4,
                py: 0.75,
                cursor: "pointer",
                fontSize: "0.84rem",
                fontWeight: 500,
                userSelect: "none",
                borderRadius: i === 0 ? "6px 0 0 6px" : "0 6px 6px 0",
                border:
                  notifTab === t
                    ? "1px solid #14b8a6"
                    : `1px solid ${
                        isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.15)"
                      }`,
                background: notifTab === t ? "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" : "transparent",
                color: notifTab === t ? "#fff" : isDark ? "#94a3b8" : "#64748b",
                transition: "all 0.18s ease",
                ...(i === 1 ? { marginLeft: "-1px" } : {}),
              }}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Rows ── */}
      <Box sx={{ flex: 1, position: "relative" }}>
        {allItems.length === 0 && (
          <Typography
            sx={{ color: "#64748b", fontSize: "0.85rem", py: 5, textAlign: "center" }}
          >
            {notifTab === "unread" ? "No unread notifications to display." : "No acknowledged notifications to display."}
          </Typography>
        )}
        {allItems.map(({ item: n, group, isFirstInGroup }, flatIndex) => (
          <Box
            key={flatIndex}
            sx={{
              position: "relative",
              borderBottom: `1px solid ${rowBorder}`,
              overflow: "visible",
              "&:last-child": { borderBottom: "none" },
              ...(rowHeight
                ? { height: rowHeight, display: "flex", alignItems: "center" }
                : {}),
            }}
          >
            {isFirstInGroup && <GroupDividerLabel label={group} />}
            <Box sx={{ width: "100%" }}>
              <DashboardNotificationItem
                {...n}
                index={flatIndex}
                total={allItems.length}
                onAcknowledge={() => onRefresh()}
                acknowledged={!isUnread}
                showGoButton={n?.hasEntity}
                refreshPage={() => onRefresh()}
              />
            </Box>
          </Box>
        ))}
      </Box>

      {NotificationListInfo && NotificationListInfo?.totalElements > totalForViewAll && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            px: 2.5,
            py: 1.2,
            borderTop: `1px solid ${rowBorder}`,
          }}
        >
          <Box
            onClick={() =>
              navigate("/notification_list", {
                state: { notificationType: notifTab },
              })
            }
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.7,
              px: 1.6,
              py: 0.6,
              borderRadius: "999px",
              cursor: "pointer",
              border: `1px solid ${isDark ? "rgba(20,184,166,0.22)" : "rgba(20,184,166,0.2)"}`,
              backgroundColor: isDark
                ? "rgba(20,184,166,0.07)"
                : "rgba(20,184,166,0.05)",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(20,184,166,0.14)"
                  : "rgba(20,184,166,0.1)",
                borderColor: "rgba(20,184,166,0.5)",
                transform: "translateX(2px)",
                boxShadow: "0 2px 12px rgba(20,184,166,0.15)",
              },
              "&:active": { transform: "translateX(0px)" },
            }}
          >
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 600,
                color: "#14b8a6",
                letterSpacing: "0.02em",
                lineHeight: 1,
              }}
            >
              View All
            </Typography>
            <ArrowForwardIos sx={{ fontSize: 9, color: "#14b8a6" }} />
          </Box>
        </Box>
      )}
    </Box>
  );
};